import * as XLSX from 'xlsx';
import { monthOffset } from '../engines/dateUtils';
import { normalizeHeader, aliasSet, monthNameToNum, parseHeaderMonth } from './templateHeaders';
import { specColumnAliases } from './templateSpecs';

const r2 = n => Math.round(n * 100) / 100;

/* MONTH_MAP, monthNameToNum and the month-header reader (parseHeaderMonth, formerly parseHeaderDate here) moved to
 * templateHeaders.js (W-062 pass 2) -- the IS/BS parser now reads month headers with the same function. */

/* THE HEADER ALIASES THIS PARSER READS -- the spec's own arrays (templateSpecs.js), by reference. The spec gate
 * asserts every entry IS the spec's array, so a hand-written list here fails the gate. Matched through the one
 * header rule (templateHeaders.normalizeHeader). */
export const VENDOR_HEADER_COLUMNS = {
    'vendor.is_category': specColumnAliases('vendor', 'is_category'),
    'vendor.display_group': specColumnAliases('vendor', 'display_group'),
    'vendor.gl_line_name': specColumnAliases('vendor', 'gl_line_name'),
    'vendor.vendor_name': specColumnAliases('vendor', 'vendor_name'),
    'prepaid.vendor_name': specColumnAliases('prepaid', 'vendor_name'),
    'prepaid.start_month': specColumnAliases('prepaid', 'start_month'),
    'prepaid.length_months': specColumnAliases('prepaid', 'length_months'),
    'prepaid.prepaid_amount': specColumnAliases('prepaid', 'prepaid_amount'),
    'prepaid.gl_line_name': specColumnAliases('prepaid', 'gl_line_name'),
    'prepaid.conference_name': specColumnAliases('prepaid', 'conference_name'),
    // 2026-10-13: the prepaid tab's section column is READ (optional) -- a row that names its section resolves its line
    // by the vendor rule (engines/prepaidRouting.js resolvePrepaidRowLine).
    'prepaid.is_category': specColumnAliases('prepaid', 'is_category'),
};
const VH = Object.fromEntries(Object.entries(VENDOR_HEADER_COLUMNS).map(([k, a]) => [k, aliasSet(a)]));

// ── Cell / value helpers ─────────────────────────────────────

function isYearLike(val) {
    const n = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
    return !isNaN(n) && n >= 1990 && n <= 2099;
}
function isMonthName(val) { return !!monthNameToNum(val); }
function cellValue(cell) {
    if (!cell) return null;
    return cell.v;
}
function isFormulaCell(cell) {
    if (!cell) return false;
    if (cell.f) return true;
    if (typeof cell.v === 'string' && cell.v.trim().startsWith('=')) return true;
    return false;
}
function cellNumeric(cell) {
    if (!cell) return 0;
    const v = cell.v;
    if (typeof v === 'number') return r2(v);
    if (typeof v === 'string') {
        const trimmed = v.trim();
        // Unevaluated formula text ("=[1]IS!A1" etc.) must not be read as a number.
        if (trimmed.startsWith('=')) return 0;
        const cleaned = trimmed.replace(/[$,()]/g, '');
        if (cleaned === '' || cleaned === '-') return 0;
        const isNeg = trimmed.includes('(') && trimmed.includes(')');
        const n = parseFloat(cleaned);
        if (isNaN(n)) return 0;
        return r2(isNeg ? -Math.abs(n) : n);
    }
    return 0;
}

// True if the label looks like a non-data row (formula ref, sheet title, etc.).
function isSkipRowLabel(label) {
    if (!label) return true;
    const t = String(label).trim();
    if (t.startsWith('=') || t.startsWith("=[")) return true; // formula reference
    const lower = t.toLowerCase();
    if (lower === 'expenses' || lower === 'income statement' || lower === 'balance sheet'
        || lower === 'cash flow' || lower === 'cash flow statement'
        || lower === 'p&l' || lower === 'profit and loss') return true;
    return false;
}
function isTotalLabel(s) {
    if (!s) return false;
    const t = String(s).toLowerCase().trim();
    return /\btotal\b/.test(t) || /\bsubtotal\b/.test(t) || /\bgrand total\b/.test(t);
}

// ── Date header detection (two-row: year row + month row; or single-row) ──

function detectDateHeaders(sheet) {
    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1:A1');
    const maxRow = Math.min(range.e.r, 25);
    const maxCol = range.e.c;

    // Strategy 0: single-row ISO dates / Excel serials / MM/DD/YYYY (most common
    // in exported expense files). Highest priority because it is unambiguous.
    for (let r = 0; r <= maxRow; r++) {
        const cols = {};
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            const val = cellValue(cell);
            const ym = parseHeaderMonth(val, cell);
            if (ym) cols[c] = ym;
        }
        if (Object.keys(cols).length >= 3) {
            return { dateColumns: cols, dataStartRow: r + 1 };
        }
    }

    let yearRow = -1;
    let monthRow = -1;
    for (let r = 0; r <= maxRow; r++) {
        let yearCount = 0, monthCount = 0;
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            const val = cellValue(cell);
            if (val == null) continue;
            if (isYearLike(val)) yearCount++;
            if (isMonthName(val)) monthCount++;
        }
        if (yearCount >= 2 && yearRow === -1) yearRow = r;
        if (monthCount >= 3 && monthRow === -1) monthRow = r;
    }

    const dateColumns = {};
    if (monthRow >= 0) {
        const effYearRow = yearRow >= 0 && yearRow <= monthRow ? yearRow : monthRow;
        let currentYear = null;
        const yearByCol = {};
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r: effYearRow, c })];
            const val = cellValue(cell);
            if (val != null && isYearLike(val)) {
                currentYear = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
            }
            if (currentYear) yearByCol[c] = currentYear;
        }
        if (sheet['!merges']) {
            for (const merge of sheet['!merges']) {
                if (merge.s.r <= effYearRow && merge.e.r >= effYearRow) {
                    const cell = sheet[XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c })];
                    const val = cellValue(cell);
                    if (val != null && isYearLike(val)) {
                        const yr = typeof val === 'number' ? val : parseInt(String(val).trim(), 10);
                        for (let c = merge.s.c; c <= merge.e.c; c++) yearByCol[c] = yr;
                    }
                }
            }
        }
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r: monthRow, c })];
            const mo = monthNameToNum(cellValue(cell));
            if (mo && yearByCol[c]) dateColumns[c] = `${yearByCol[c]}-${mo}`;
        }
        if (Object.keys(dateColumns).length >= 3) {
            return { dateColumns, dataStartRow: monthRow + 1 };
        }
    }

    // Single-row fallback ("Jan 2021", "Jan-21")
    for (let r = 0; r <= maxRow; r++) {
        const cols = {};
        for (let c = 0; c <= maxCol; c++) {
            const cell = sheet[XLSX.utils.encode_cell({ r, c })];
            const val = cellValue(cell);
            if (val == null) continue;
            const s = String(val).trim();
            const m = s.match(/^([A-Za-z]+)[\s\-./]+(\d{2,4})$/);
            if (m) {
                const mo = monthNameToNum(m[1]);
                let yr = parseInt(m[2], 10);
                if (yr < 100) yr += 2000;
                if (mo && yr >= 1990 && yr <= 2099) cols[c] = `${yr}-${mo}`;
            }
            if (cell && cell.t === 'd') {
                const d = cell.v instanceof Date ? cell.v : new Date(cell.v);
                // Use UTC to avoid TZ drift on display-only month extraction
                const yr = d.getUTCFullYear();
                const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
                if (yr >= 1990 && yr <= 2099) cols[c] = `${yr}-${mo}`;
            }
        }
        if (Object.keys(cols).length >= 3) {
            return { dateColumns: cols, dataStartRow: r + 1 };
        }
    }

    return { dateColumns: {}, dataStartRow: 0 };
}

// ── Expenses sheet parser ───────────────────────────────────

function parseExpensesSheet(sheet) {
    const flags = [];
    const vendors = [];
    if (!sheet) return { vendors, months: [], flags: [{ type: 'info', row: 0, label: 'No expenses sheet found' }] };

    // Two passes: formatted strings for label/date detection, raw for numeric amounts.
    const rowsFormatted = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: 'YYYY-MM' });
    const rowsRaw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

    // Detect date header row: first row with ≥3 cells parseable as YYYY-MM.
    let dataStartRow = -1;
    let dateColumns = {};
    const scanMaxRow = Math.min(Math.max(rowsFormatted.length, rowsRaw.length), 25);
    for (let r = 0; r < scanMaxRow; r++) {
        const fRow = rowsFormatted[r] || [];
        const rRow = rowsRaw[r] || [];
        const maxC = Math.max(fRow.length, rRow.length);
        const cols = {};
        for (let c = 0; c < maxC; c++) {
            // Prefer the formatted cell (handles "Jan-21" display), fall back to raw (handles serial numbers).
            const ym = parseHeaderMonth(fRow[c]) || parseHeaderMonth(rRow[c]);
            if (ym) cols[c] = ym;
        }
        if (Object.keys(cols).length >= 3) {
            dateColumns = cols;
            dataStartRow = r + 1;
            break;
        }
    }
    /* Batch 1b (2026-11-03, L3599): a Pre-Launch company's Expenses sheet has ONE month column (its window is one month), and a
     * young company's may have two. The >= 3 rule above stays FIRST, so a sheet with 3+ month columns is read exactly as before.
     * Only when no row has 3: the first row that carries a template header (is_category / display_group / gl_line_name /
     * vendor_name) and at least one month is the header; failing that, the first row with at least one month. */
    if (dataStartRow < 0) {
        let firstAny = null;
        for (let r = 0; r < scanMaxRow && dataStartRow < 0; r++) {
            const fRow = rowsFormatted[r] || [];
            const rRow = rowsRaw[r] || [];
            const maxC = Math.max(fRow.length, rRow.length);
            const cols = {};
            let isHeader = false;
            for (let c = 0; c < maxC; c++) {
                const ym = parseHeaderMonth(fRow[c]) || parseHeaderMonth(rRow[c]);
                if (ym) { cols[c] = ym; continue; }
                const h = normalizeHeader(fRow[c]);
                if (VH['vendor.is_category'].has(h) || VH['vendor.display_group'].has(h) || VH['vendor.gl_line_name'].has(h) || VH['vendor.vendor_name'].has(h)) isHeader = true;
            }
            if (!Object.keys(cols).length) continue;
            if (isHeader) { dateColumns = cols; dataStartRow = r + 1; }
            else if (!firstAny) firstAny = { cols, r };
        }
        if (dataStartRow < 0 && firstAny) { dateColumns = firstAny.cols; dataStartRow = firstAny.r + 1; }
    }

    const months = [...new Set(Object.values(dateColumns))].sort();
    if (months.length === 0) {
        flags.push({ type: 'no_dates', row: 0, label: 'Could not detect any month column in the expenses sheet -- the header row needs at least one month (e.g. 2026-10)' });
        return { vendors, months, flags };
    }

    const dateColEntries = Object.entries(dateColumns)
        .map(([c, m]) => ({ col: parseInt(c, 10), month: m }))
        .sort((a, b) => a.col - b.col);
    const firstDateCol = dateColEntries[0].col;
    // Label scan range: cols A–C (indices 0–2) or up to firstDateCol, whichever is smaller.
    const labelScanMax = Math.min(3, firstDateCol);

    let currentCategory = '';
    let specScan = null;

    // ★ TEMPLATES PASS — detect the canonical header-named layout on the date-header row:
    //   is_category | display_group | gl_line_name | vendor_name | <ISO months>.
    // When present, read the vendor from the vendor_name COLUMN (not the first text cell, which is
    // is_category in this layout) and the category from is_category. Same header-name approach as
    // parsePrepaidSheet (4c). Otherwise fall back to the legacy first-text-cell / col-A-category logic.
    let canonCols = null;
    {
        const hdr = rowsFormatted[dataStartRow - 1] || [];
        const cc = { is_category: null, display_group: null, gl_line_name: null, vendor_name: null };
        for (let c = 0; c < hdr.length; c++) {
            const h = normalizeHeader(hdr[c]);
            if (cc.is_category == null && VH['vendor.is_category'].has(h)) cc.is_category = c;
            else if (cc.display_group == null && VH['vendor.display_group'].has(h)) cc.display_group = c;
            else if (cc.gl_line_name == null && VH['vendor.gl_line_name'].has(h)) cc.gl_line_name = c;
            else if (cc.vendor_name == null && VH['vendor.vendor_name'].has(h)) cc.vendor_name = c;
        }
        if (cc.vendor_name != null && cc.is_category != null) canonCols = cc;
        /* W-068 -- what the template check reads: the header->column map this parser found and the raw rows (row i is
         * spreadsheet row i + 1). A header row that has is_category but no vendor_name is a BROKEN template (the legacy
         * reader below would misread it) -- and so is one with gl_line_name but no is_category: recorded as such, so the
         * check names the missing column. */
        if (canonCols) specScan = { template: 'vendor', layout: 'template', headerRow: dataStartRow - 1, cols: { ...cc }, monthCols: dateColumns, rows: rowsRaw };
        // is_category or gl_line_name marks the template (a legacy export carries neither; "Vendor" alone does not count)
        else if (cc.is_category != null || cc.gl_line_name != null) specScan = { template: 'vendor', layout: 'broken', headerRow: dataStartRow - 1, cols: { ...cc }, monthCols: dateColumns, rows: rowsRaw };

        /* ★★ §E1 — THE SAME DELETED-COLUMN GUARD AS parseFinancialExcel's, FOR THE TWO
         * OPTIONAL COLUMNS THAT COLLAPSE THE SAME WAY. `cc.<col>` is an index when the header
         * is present and null when it is absent; every per-row read turns both that and an
         * empty cell into null, and `cc` never leaves this block. ⛔ Ask here or never.
         *
         * ⓘ GUARDED ON `canonCols`, DELIBERATELY: a LEGACY expenses sheet has no canonical
         *   header row at all, so these columns do not exist as a concept there and a notice
         *   would fire on every legacy upload — correct data, wrongly flagged.
         * ⛔ INFORMATIONAL, NEVER BLOCKING, and ONCE PER SHEET — both columns are genuinely
         *   optional and a tenant may legitimately have neither.
         * ⓘ No renderer change: Step 6's review prints `message || label` generically. */
        if (canonCols) {
            if (canonCols.display_group == null) {
                flags.push({
                    type: 'no_display_group_column',
                    row: dataStartRow,
                    label: 'display_group',
                    message: 'This sheet has no display_group column. If your expense lines have no '
                        + 'sub-groups that is fine — continue. If you removed the column, re-download '
                        + 'the template and re-upload, or set the groups yourself at Step 3 after Step 5.',
                });
            }
            /* ★ THIS ONE HAS ALREADY COST A REAL DEFECT, so its sentence says what is LOST
             * rather than merely that a column is missing. The per-row gl_line_name was once
             * located and then discarded (§8 / 6c, fixed 6fc2ab0): without it every vendor in
             * a section collapses onto ONE catch-all IS line, which capped a 229-vendor tenant
             * at 5 lines against cid=3's 34. Deleting the column reinstates exactly that cap,
             * silently — the import succeeds and the expense detail is simply gone. */
            if (canonCols.gl_line_name == null) {
                flags.push({
                    type: 'no_gl_line_name_column',
                    row: dataStartRow,
                    label: 'gl_line_name',
                    severity: 'warning',
                    message: 'This sheet has no gl_line_name column, so every vendor in a section will '
                        + 'roll into ONE catch-all expense line instead of its own. If that is what you '
                        + 'want, continue. If you removed the column, re-download the template and '
                        + 're-upload — the per-vendor detail cannot be recovered afterwards.',
                });
            }
        }
    }

    for (let r = dataStartRow; r < rowsFormatted.length; r++) {
        const fRow = rowsFormatted[r] || [];
        const rRow = rowsRaw[r] || [];

        // Label + category. Canonical: read by column NAME (vendor_name / is_category / display_group;
        // a blank display_group → null). Legacy: first non-empty text cell in cols A–C.
        let label = '', labelCol = -1, canonCategory = null, canonDg = null, canonLine = null;
        if (canonCols) {
            const vn = fRow[canonCols.vendor_name];
            label = vn == null ? '' : String(vn).trim();
            const ic = fRow[canonCols.is_category];
            canonCategory = (ic == null || String(ic).trim() === '') ? null : String(ic).trim();
            if (canonCols.display_group != null) {
                const dg = fRow[canonCols.display_group];
                canonDg = (dg == null || String(dg).trim() === '') ? null : String(dg).trim();
            }
            /* ★ THE PER-LINE COLUMN. canonCols.gl_line_name has been LOCATED since the
             * canonical-format rewrite and then never read — so the template's 33
             * distinct line names collapsed to the 5 is_category literals and Step 6
             * could only ever route vendors to 5 IS lines instead of cid=3's 34.
             * Carrying it is the whole granularity fix; the key itself is derived
             * downstream by the ONE shared buildVendorIsLineKey (utils/isLineKey). */
            /* ★ BLANK ('') vs ABSENT (null) IS LOAD-BEARING, do not collapse them.
             * null  = the sheet has NO gl_line_name column (a legacy layout) -> Step 6
             *         falls back to its per-category behaviour.
             * ''    = the column EXISTS and this row's cell is empty -> that is a data
             *         problem on a canonical upload and must surface as a required-review
             *         row, NOT silently drop into the legacy branch. */
            if (canonCols.gl_line_name != null) {
                const gl = fRow[canonCols.gl_line_name];
                canonLine = (gl == null) ? '' : String(gl).trim();
            }
        } else {
            for (let c = 0; c < labelScanMax; c++) {
                const v = fRow[c];
                if (v != null && String(v).trim() !== '') { label = String(v).trim(); labelCol = c; break; }
            }
        }
        if (!label) continue;
        // "Total", "Subtotal", "Grand Total" -- subtotal rows, never vendors -- on the LEGACY layout only.
        /* W-068 F1 (Brian 09-29): on the TEMPLATE a vendor whose name looks like a total ("Total Wine") is KEPT and
         * flagged by the template check (Step 6 has no way to leave a vendor out, so it is imported). This read
         * `if (isTotalLabel(label)) continue;` for both layouts -- the vendor vanished without a word. */
        if (!canonCols && isTotalLabel(label)) continue;
        if (isSkipRowLabel(label)) continue;

        // Amounts come from the raw row so we get real numbers.
        const amounts = {};
        let nonZeroCount = 0;
        let formulaCount = 0;
        for (const { col, month } of dateColEntries) {
            const v = rRow[col];
            if (v == null || v === '') { amounts[month] = 0; continue; }
            if (typeof v === 'object') { amounts[month] = 0; continue; } // Date / Error cell
            if (typeof v === 'string') {
                const trimmed = v.trim();
                if (trimmed.startsWith('=')) { amounts[month] = 0; formulaCount++; continue; }
                const cleaned = trimmed.replace(/[$,()]/g, '');
                if (cleaned === '' || cleaned === '-') { amounts[month] = 0; continue; }
                const parsed = parseFloat(cleaned);
                if (isNaN(parsed)) { amounts[month] = 0; continue; }
                const isNeg = trimmed.includes('(') && trimmed.includes(')');
                const n = r2(isNeg ? -Math.abs(parsed) : parsed);
                amounts[month] = n;
                if (n !== 0) nonZeroCount++;
            } else if (typeof v === 'number') {
                const n = r2(v);
                amounts[month] = n;
                if (n !== 0) nonZeroCount++;
            } else {
                amounts[month] = 0;
            }
        }

        if (canonCols) {
            // Canonical: a row with a vendor_name IS a vendor (keep all-zero rows too — a real
            // vendor that booked $0 in history is still a vendor, and its category is explicit).
            if (formulaCount > 0) {
                flags.push({ type: 'formula', row: r + 1, label, message: `${label}: row uses formulas — values read at current result` });
            }
            vendors.push({
                vendor_name: label,
                /* ★ §E1 (STAGE 2) — NO INVENTED CATEGORY. This read
                 * `canonCategory || '(Uncategorized)'`, turning a blank is_category
                 * CELL into a category-SHAPED string. The blank is already known one
                 * line up (:305 sets canonCategory to null for an empty cell) and was
                 * being thrown away here. §E1: a classification comes from the
                 * operator's explicit template column; where it is absent the wizard
                 * flags the row and the operator chooses. Emitting the real null is
                 * what lets it do that honestly — Step 6c already flags and BLOCKS the
                 * row (rowUnsure), and the 6c category <select> resolves null to its
                 * '—' option instead of showing a phantom value that is not in the
                 * list. No flag is pushed here: the 6c assembly already emits
                 * `vendor_line_unsure` for exactly these rows, so a parser flag would
                 * be a second notice for one fault. */
                gl_category: canonCategory,
                display_group: canonDg,
                // null on the LEGACY layout (no such column) — Step 6 falls back to its
                // per-category behaviour for those tenants rather than deriving a key
                // from nothing. One branch, not a second screen.
                gl_line_name: canonLine,
                monthly_amounts: amounts,
            });
            continue;
        }

        // Legacy — row with no non-zero amounts:
        //   • label in col A → treat as a GL category header ("ISP Fees").
        //   • label in col B/C (vendor position) → skip entirely. A zero-only
        //     vendor row ("Other job boards" with a single 0) would otherwise
        //     overwrite the real category header.
        if (nonZeroCount === 0) {
            if (labelCol === 0) currentCategory = label;
            continue;
        }

        if (formulaCount > 0) {
            flags.push({ type: 'formula', row: r + 1, label, message: `${label}: row uses formulas — values read at current result` });
        }

        vendors.push({
            vendor_name: label,
            /* ★ §E1 (STAGE 2) — same rule on the legacy layout: a sheet with no
             * col-A section header emits null, not an invented label.
             * ⓘ THE STORED KEY DOES NOT MOVE HERE. catMapKey's legacy branch
             * (AdminPage.js:414) re-coalesces `v.gl_category || '(Uncategorized)'`,
             * so a legacy row's vendor_category_mappings key is byte-identical
             * before and after this change. Verified in Fixture C. */
            gl_category: currentCategory || null,
            monthly_amounts: amounts,
        });
    }

    return specScan ? { vendors, months, flags, specScan } : { vendors, months, flags };
}

// ── Pattern detection ───────────────────────────────────────

// Principle 0: month arithmetic has ONE implementation — engines/dateUtils.
// The local monthsDiff/addMonths copies that used to live here were deleted:
// monthsDiff was `(by-ay)*12 + (bm-am)`, which is dateUtils.monthOffset's
// `(by*12+bm-1) - (ay*12+am-1)` rearranged (same arg order: months FROM a TO b);
// addMonths was dateUtils.addMonths WITHOUT its positive-modulo guard, so it
// produced a malformed "YYYY-00" once total = y*12+(m-1)+n went negative.

function detectPattern(monthlyAmounts, allMonths) {
    const nonZero = allMonths
        .filter(m => (monthlyAmounts[m] || 0) > 0)
        .sort();
    if (nonZero.length === 0) return { freq: 'manual', paymentMonths: [], gaps: [] };
    if (nonZero.length === 1) return { freq: 'manual', paymentMonths: nonZero, gaps: [] };

    const gaps = [];
    for (let i = 1; i < nonZero.length; i++) gaps.push(monthOffset(nonZero[i - 1], nonZero[i]));
    const counts = {};
    for (const g of gaps) counts[g] = (counts[g] || 0) + 1;
    const modeGap = Number(Object.keys(counts).reduce((a, b) => counts[a] >= counts[b] ? a : b));
    const modeShare = counts[modeGap] / gaps.length;

    let freq = 'manual';
    // Require a dominant cadence (at least 70% of gaps match the mode) before claiming a pattern.
    if (modeShare >= 0.7) {
        if (modeGap === 1) freq = 'monthly';
        else if (modeGap === 3) freq = 'quarterly';
        else if (modeGap === 6) freq = 'semi_annual';
        else if (modeGap === 12) freq = 'annual';
    }
    return { freq, paymentMonths: nonZero, gaps };
}

// Recent-history helpers used for default forecast amounts.
// last3Avg: mean of the last 3 observed months regardless of value. If the last
// three are all zero, L3M Avg = 0 — user sees the vendor is inactive and can
// mark as Skip.
function last3Avg(monthlyAmounts, allMonths) {
    const sortedMonths = [...allMonths].sort();
    const last3 = sortedMonths.slice(-3);
    if (last3.length === 0) return 0;
    const sum = last3.reduce((s, m) => s + (Number(monthlyAmounts[m]) || 0), 0);
    return r2(sum / last3.length);
}
// lastNonZeroMonth still uses the non-zero filter — this feeds prepaid cycle
// projection where we need the last month with actual spend.
function lastNonZeroMonth(monthlyAmounts, allMonths) {
    const nonZero = allMonths
        .filter(m => {
            const v = monthlyAmounts[m];
            return typeof v === 'number' && v !== 0;
        })
        .sort();
    return nonZero.length > 0 ? nonZero[nonZero.length - 1] : null;
}
function lastNonZeroAmount(monthlyAmounts, allMonths) {
    const m = lastNonZeroMonth(monthlyAmounts, allMonths);
    return m ? r2(monthlyAmounts[m] || 0) : 0;
}

// ── Prepaid sheet parser ────────────────────────────────────

export function parseStartMonth(val) {
    if (val == null || val === '') return null;
    if (typeof val === 'number') {
        // Excel serial → unix ms via arithmetic (no new Date("YYYY-MM-DD"))
        const ms = (val - 25569) * 86400 * 1000;
        const d = new Date(ms);
        if (isNaN(d.getTime())) return null;
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    }
    const s = String(val).trim();
    const iso = /^(\d{4})-(\d{1,2})/.exec(s);
    if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}`;
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
    if (us) {
        let [, m, , y] = us;
        if (y.length === 2) y = (parseInt(y, 10) > 50 ? '19' : '20') + y;
        return `${y}-${m.padStart(2, '0')}`;
    }
    const mName = /^([A-Za-z]+)[\s\-./]+(\d{2,4})$/.exec(s);
    if (mName) {
        const mo = monthNameToNum(mName[1]);
        let y = parseInt(mName[2], 10);
        if (y < 100) y += 2000;
        if (mo && y >= 1990 && y <= 2099) return `${y}-${mo}`;
    }
    return null;
}

// Structure (confirmed):
//   Row N:   [null, "Vendor", "IS Account", "Starting Date", "Length", "Prepaid", null, <date headers...>]
//   Row N+k: ["eShares (Carta)"  ...]                        ← vendor group header (col A only)
//   Row N+k+1: [null, null, "Dues and Subs", "2020-06-15", "6", "1000", null, <monthly amort...>]
// Header is identified by col B (index 1) === "Vendor".
// Group headers have col A set AND col C empty. Contract rows have col A empty AND col C set.
// Date columns live at index 6+; monthly amort amounts live in the same cols on contract rows.
function parsePrepaidSheet(sheet) {
    const flags = [];
    const contracts = [];
    const contractRows = []; // parallel: source spreadsheet row (1-based) per contract
    if (!sheet) return { contracts, flags };

    const rowsFormatted = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: 'YYYY-MM' });
    const rowsRaw = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true });

    // ── Step 1: resolve columns BY HEADER NAME, not fixed position (Build 4c / Finding 1).
    // The old parser hard-coded col B == "Vendor" + fixed offsets, so it returned EMPTY on
    // the operator's WIDE template (col B = "conference_name"). Now match header TEXT, so
    // both the WIDE flat layout (vendor_name | conference_name | is_category | display_group
    // | gl_line_name | start_month | length_months | prepaid_amount | <months>) AND the
    // legacy NARROW grouped layout (Vendor | IS Account | Starting Date | Length | Prepaid |
    // <dates>) parse. Optional cols (conference_name / is_category / display_group) may be
    // present / empty / absent / reordered without breaking. ★ PREPAID PARSER ONLY — the
    // IS/BS/vendor parsers are the deferred "templates pass".
    let headerRow = -1;
    const col = {};
    let best = null; // W-068: the row with the most prepaid headers, for a sheet whose header row is incomplete
    for (let r = 0; r < Math.min(rowsFormatted.length, 30); r++) {
        const row = rowsFormatted[r] || [];
        const idx = {};
        for (let c = 0; c < row.length; c++) {
            const h = normalizeHeader(row[c]);
            if (!h) continue;
            if (idx.vendor == null && VH['prepaid.vendor_name'].has(h)) idx.vendor = c;
            // KEPT IN THE PARSER: "any header beginning start" is a PATTERN, not a list (recorded in the spec as the
            // column's aliasRule; the gate tests its sample header).
            else if (idx.start == null && (VH['prepaid.start_month'].has(h) || h.startsWith('start'))) idx.start = c;
            else if (idx.length == null && VH['prepaid.length_months'].has(h)) idx.length = c;
            else if (idx.prepaid == null && VH['prepaid.prepaid_amount'].has(h)) idx.prepaid = c;
            else if (idx.account == null && VH['prepaid.gl_line_name'].has(h)) idx.account = c;
            else if (idx.conference == null && VH['prepaid.conference_name'].has(h)) idx.conference = c;
            // 2026-10-13: the OPTIONAL section column. LAST in the chain, and NOT counted in `hits` below, so which row is
            // taken as the header row -- and which sheets are recorded as a broken template -- is exactly as before.
            else if (idx.category == null && VH['prepaid.is_category'].has(h)) idx.category = c;
        }
        if (idx.vendor != null && idx.start != null && idx.length != null && idx.prepaid != null) {
            headerRow = r; Object.assign(col, idx); break;
        }
        const hits = Object.keys(idx).filter(k => k !== 'category').length;
        if (hits >= 2 && (!best || hits > best.hits)) best = { r, idx, hits };
    }
    // W-068 -- the spec column names of the indices above, for the template check.
    const scanCols = (idx) => ({ vendor_name: idx.vendor ?? null, conference_name: idx.conference ?? null, is_category: idx.category ?? null,
        gl_line_name: idx.account ?? null,
        start_month: idx.start ?? null, length_months: idx.length ?? null, prepaid_amount: idx.prepaid ?? null });
    if (headerRow === -1) {
        flags.push({ type: 'info', row: 0, label: 'No prepaid header row detected (need Vendor + Start + Length + Prepaid columns)' });
        /* W-068: a row carrying two or more of the prepaid headers is a BROKEN template (today it imports zero contracts
         * behind the notice above) -- recorded so the check names the missing columns. */
        return best
            ? { contracts, flags, specScan: { template: 'prepaid', layout: 'broken', headerRow: best.r, cols: scanCols(best.idx), monthCols: {}, rows: rowsRaw } }
            : { contracts, flags };
    }

    /* ★★ §E1 — THE SAME DELETED-COLUMN GUARD AS parseExpensesSheet's ABOVE, FOR THE ONE
     * OPTIONAL COLUMN ON THIS SHEET THAT COLLAPSES THE SAME WAY. `col.account` is an index
     * when the header is present and undefined when it is absent; the per-row read below
     * turns BOTH that and an empty cell into the vendor name. ⛔ Ask here or never.
     *
     * ★ MEASURED (diffs/prepaid-optcols-phase0.txt): a DELETED gl_line_name column and a
     *   BLANK one produce byte-identical contracts AND byte-identical flags on both real
     *   workbooks. After the fallback at the per-row read the two are indistinguishable,
     *   so no downstream check can recover the difference — the a9680f9 condition, in a
     *   third parser.
     *
     * ★ THIS ONE SAYS WHAT IS LOST, not merely that a column is missing — same reason its
     *   expenses-sheet sibling does. Without the column every contract's `account` becomes
     *   its VENDOR NAME, and a vendor name is not an IS-leaf label: measured, contracts
     *   that resolve to an IS line collapse 169→20 (cid=3) and 167→19 (cid=8). The engine
     *   then SKIPS each unresolved contract at $0 (runFullModel §12b) behind a console.warn
     *   — a warning with no reader.
     * ⛔ NO DOLLAR FIGURE IN THE MESSAGE, DELIBERATELY: the measured sums are historical
     *   months and §12b books FORECAST only, so any figure quoted here would be wrong.
     *
     * ★ UNGATED, AND THAT IS THE DIFFERENCE FROM THE SIBLING. parseExpensesSheet guards on
     *   `canonCols` because a LEGACY expenses sheet has no canonical header row at all, so
     *   its columns are not a concept there. THIS parser has no such case: it accepts only
     *   header-named layouts, and BOTH documented layouts carry the account column — the
     *   WIDE flat one as `gl_line_name`, the legacy NARROW grouped one as `IS Account`,
     *   and `is_account` is one of the three spellings resolved above. So `col.account ==
     *   null` means the same thing in every accepted layout: the column is genuinely gone.
     * ⛔ INFORMATIONAL, NEVER BLOCKING, and ONCE PER SHEET.
     * ⓘ No renderer change: Step 6b prints `message || label` generically, and
     *   assembleVendorResult stamps `sheet` on every prepaid flag as it merges.
     *
     * ★ A DISTINCT SLUG, NOT the sibling's `no_gl_line_name_column`, and the reason is a
     *   live pattern rather than tidiness: AdminPage does
     *   `allFlags.find(f => f.type === 'no_display_group_column')` — a SINGLE-RESULT find
     *   keyed on a type that two parsers already emit. Sharing a slug here would make the
     *   two notices indistinguishable to any such consumer, and the only thing separating
     *   them would be `f.sheet` — which is the OPERATOR'S SHEET NAME (pickPrepaidSheet
     *   matches /prepaid/i, so it can be anything), a display string, not a stable key.
     *   The two describe materially different losses: one caps expense DETAIL, this one
     *   stops contracts BOOKING. Same family, same shape, distinguishable by type alone. */
    if (col.account == null) {
        flags.push({
            type: 'no_prepaid_gl_line_name_column',
            row: headerRow + 1,
            label: 'gl_line_name',
            severity: 'warning',
            message: 'This prepaid sheet has no gl_line_name column, so every contract falls back '
                + 'to its own vendor name as the IS account. A vendor name is not an income-statement '
                + 'line, so those contracts will not resolve to one and their amortization will book '
                + 'nothing to the P&L — the prepaid balance still rolls on the balance sheet, so the '
                + 'import will look like it worked. Re-download the template and re-upload with the '
                + 'column filled in.',
        });
    }

    // Step 2: month columns = any header (NOT one of the resolved fixed cols) that parses as YYYY-MM.
    const fHeader = rowsFormatted[headerRow] || [];
    const rHeader = rowsRaw[headerRow] || [];
    const sheetRange = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : null;
    const maxC = Math.max(fHeader.length, rHeader.length, sheetRange ? sheetRange.e.c + 1 : 0);
    const fixedCols = new Set([col.vendor, col.start, col.length, col.prepaid, col.account, col.conference].filter(x => x != null));
    const dateColumns = {}; // colIdx → "YYYY-MM"
    for (let c = 0; c < maxC; c++) {
        if (fixedCols.has(c)) continue;
        const ym = parseHeaderMonth(fHeader[c]) || parseHeaderMonth(rHeader[c]);
        if (ym) dateColumns[c] = ym;
    }

    // Step 3: parse data rows — handles BOTH structures via one rule:
    //   • A row is a CONTRACT when its START column parses as a date.
    //     vendor = the row's own vendor cell if present (WIDE flat layout, vendor per row),
    //     else the last-seen group header (NARROW grouped layout).
    //   • A row with a vendor but NO start date is a GROUP HEADER → set currentVendor
    //     (skipped when it's a Total/footer label).
    //   • Anything else → skip silently.
    let currentVendor = '';
    for (let r = headerRow + 1; r < rowsFormatted.length; r++) {
        const fRow = rowsFormatted[r] || [];
        const rRow = rowsRaw[r] || [];

        const vendorCell = fRow[col.vendor];
        const vendorStr = vendorCell != null && String(vendorCell).trim() !== '' ? String(vendorCell).trim() : '';
        const startRaw = (fRow[col.start] != null && fRow[col.start] !== '') ? fRow[col.start] : rRow[col.start];
        const startMonth = parseStartMonth(startRaw);

        if (!startMonth) {
            // No start date → a group header (grouped layout) or a total/blank row.
            if (vendorStr && !isTotalLabel(vendorStr) && !isSkipRowLabel(vendorStr)) currentVendor = vendorStr;
            continue;
        }

        // Contract row.
        const vendor = vendorStr || currentVendor;
        if (!vendor || isTotalLabel(vendor) || isSkipRowLabel(vendor)) continue;

        const accountRaw = col.account != null ? fRow[col.account] : null;
        const account = accountRaw != null && String(accountRaw).trim() !== ''
            ? String(accountRaw).trim()
            : vendor; // IS Account optional → fall back to the vendor name
        // 4c: conference_name is TRANSIENT — it powers the precise event guard (0a) + the
        // Step-6 conference checklist; it is NOT a DB column and is never persisted.
        const conference_name = col.conference != null && fRow[col.conference] != null && String(fRow[col.conference]).trim() !== ''
            ? String(fRow[col.conference]).trim() : null;
        /* 2026-10-13 (Brian, SubBase): THE ROW'S SECTION, when the template names one. Carried as written (trimmed,
         * case kept -- the upload check compares it exactly, as it does for a vendor row) and resolved downstream by the
         * ONE owner (engines/prepaidRouting.js resolvePrepaidRowLine): section + name -> the vendor rule's key. The
         * parser knows no company, so it resolves nothing itself.
         * THE KEY IS ABSENT -- not null -- when the sheet has no such column or the cell is blank, so a contract that
         * names no section is the very object this parser produced before, and takes the name-only rule as before. */
        const sectionRaw = col.category != null ? fRow[col.category] : null;
        const section = sectionRaw != null && String(sectionRaw).trim() !== '' ? String(sectionRaw).trim() : '';

        const lengthRaw = rRow[col.length];
        const lengthNum = typeof lengthRaw === 'number'
            ? Math.round(lengthRaw)
            : parseInt(String(lengthRaw || '').replace(/[^0-9]/g, ''), 10);

        const prepaidRaw = rRow[col.prepaid];
        const prepaidAmt = typeof prepaidRaw === 'number'
            ? r2(prepaidRaw)
            : r2(parseFloat(String(prepaidRaw || '').replace(/[$,()]/g, '')) || 0);

        const amort = {};
        for (const [c, month] of Object.entries(dateColumns)) {
            const v = rRow[parseInt(c, 10)];
            /* ★★ P3-B ITEM 1 — BLANK, GENUINE ZERO AND GARBAGE ARE NOW THREE DIFFERENT THINGS.
             *
             * WHAT THIS USED TO BE: `let n = 0;` then two branches that both fell back to 0,
             * followed by `if (n !== 0) amort[month] = n;`. THREE INPUTS COLLAPSED TO ONE
             * OUTPUT — a blank cell, an operator-typed 0, and unparseable garbage all arrived
             * as 0 and were all dropped. The parser had no blank-vs-zero discrimination at
             * all, and the comment below said so and deferred it. This is that deferral.
             *
             * ★ THE RULE (§E1, Brian's ruling A1): A VALUE COMES FROM AN EXPLICIT OPERATOR
             *   MARK; SILENCE IS NEVER READ AS AN ANSWER.
             *     · a GENUINE numeric cell — a number, or a string that parses — is a
             *       STATEMENT. Store the key, INCLUDING when the value is 0: "this month
             *       amortizes nothing" is something the operator said.
             *     · a BLANK / empty / absent cell is SILENCE. Emit no key.
             *     · NON-NUMERIC / unparseable text is also silence, not a zero. Emit no key.
             *       ⛔ The old `parseFloat(x) || 0` turned garbage INTO a stored 0; that is
             *       exactly the "infer an answer from a non-answer" move §E1 forbids, and it
             *       is why the `|| 0` is gone rather than merely relocated.
             *
             * ★ WHY IT MATTERS DOWNSTREAM — the key, not the value, is what is load-bearing.
             *   `fillForecastPrepaidAmort`'s first guard is
             *       `if (Object.keys(amort).some(k => k > cutoff)) return { generated: false }`
             *   — a KEY-PRESENCE test whose comment already claims "workbook supplied
             *   forecast — never clobber". Until now the parser could not tell it the truth:
             *     · a typed 0 in a FORECAST month now emits a key -> the guard FIRES -> the
             *       fill DECLINES -> the operator's zero STANDS.
             *     · a blank forecast month still emits no key -> the guard is silent -> the
             *       fill GENERATES -> the generator fills the SILENCE.
             *   The generator fills what the operator did not say and defers to what they
             *   did. MEASURED on a 12-month row with one typed 0 at 2023-04: the two arms
             *   differ by $600.00 of forecast expense.
             *
             * ⓘ MEASURED EFFECT ON TODAY'S DATA: $0.00. The committed workbook holds exactly
             *   four genuine zeros, all on one Figma row whose `prepaid_amount` is 0 and all
             *   ≤ the 2023-03 cutoff — so `!(amt !== 0)` declines before the forecast guard
             *   is ever consulted. The four cells are stored and nothing moves.
             * ⛔ NO ROW-LEVEL CHANGE (ruling B1). An all-zero row still becomes a contract
             *   exactly as before; nothing here flags or blocks it. Only the CELL guard moved.
             * ⓘ ALSO CHANGES WHAT THE SHIPPED OPERATOR GATE SEES, deliberately:
             *   `reconcilePrepaidImport` keys on key-presence too, so a stored $0.00 forecast
             *   cell now counts as an at-risk cell it will name. That is a decision recorded
             *   in diffs/phase3b-phase0.txt §3a, not a side effect. */
            let n = null;                       // null = NO STATEMENT (emit no key)
            if (typeof v === 'number') {
                n = r2(v);                      // a numeric cell — 0 included
            } else if (typeof v === 'string') {
                const trimmed = v.trim();
                if (trimmed !== '' && !trimmed.startsWith('=')) {
                    const parsed = parseFloat(trimmed.replace(/[$,()]/g, ''));
                    if (!isNaN(parsed)) n = r2(parsed);   // a numeric STRING — 0 included
                    // NaN -> stays null: garbage is silence, never a stored 0
                }
                // '' or a formula string -> stays null (unchanged: formulas were always skipped)
            }
            // anything else (null / undefined / boolean / Date) -> stays null
            /* ★★ THE CREDIT FIX — `n > 0` DISCARDED EVERY NEGATIVE AMORTIZATION CELL.
             * A prepaid schedule can legitimately carry a CREDIT month (a refund, a credit memo,
             * a true-up). This guard was doing two jobs and only one of them on purpose: skipping
             * blank / non-numeric cells (which it still does — those land on `n = 0` above), and,
             * accidentally, skipping credits. MEASURED on the committed workbook: BOTH of AE
             * Model's credit rows — Servicenow (Lightstep) -2,344.13 and Salesforce.com marketing
             * -6,107.00 — parsed to an EMPTY dict, which is why cid=8/cid=40 store `{}` for
             * Servicenow while cid=3 (imported by the Python 4d migration, which had no such
             * filter) stores its two cells.
             * ★ WHY IT MATTERS BEYOND THOSE TWO ROWS, AND WHY IT IS NOT COSMETIC: a WHOLLY
             * negative credit is harmless — `fillForecastPrepaidAmort` rebuilds the forecast half
             * from the TERMS, so a forecast-only or straddling credit comes out right anyway. The
             * damage is on a MIXED-SIGN row: drop the credit month out of an otherwise positive
             * schedule and the fill's first guard (`some(k => k > cutoff)` — "the workbook
             * supplied a forecast, never clobber") sees the surviving positive cells, concludes
             * the forecast is complete, and declines. MEASURED on a 12-month row with one
             * -$2,000 forecast credit: **+$2,000 of phantom forecast expense, silently.**
             * ⛔ DELIBERATELY NOT `n != null` OR A PRESENCE TEST: an explicit stored ZERO is still
             * skipped. Capturing it needs blank-vs-0 discrimination, which changes the parser's
             * contract for all 131 positive rows on this workbook. Separate decision, separate
             * commit (§6 Mantra 6). This line changes exactly one thing: the SIGN stops mattering.
             * Full investigation: diffs/servicenow-credit-import-investigation.txt
             * ⓘ 2026-08-24, P3-B item 1: that deferred "separate decision, separate commit" is
             *   THIS one. The guard is now `n !== null` — presence, not non-zero-ness — so the
             *   SIGN still does not matter (33ad56f preserved) and the ZERO now does. */
            if (n !== null) amort[month] = n;
        }

        contracts.push({
            vendor_name: vendor,
            account,
            ...(section ? { is_category: section } : {}),
            conference_name,
            start_month: startMonth,
            length_months: !isNaN(lengthNum) ? lengthNum : null,
            prepaid_amount: prepaidAmt,
            amort,
            is_historical: true,
        });
        contractRows.push(r + 1); // 1-based spreadsheet row for the notice
    }

    // ── Import-integrity notice (non-blocking): DUPLICATE CONTRACT KEY ──
    // The onboarding import upserts on (company_id, vendor_name, start_month, length_months,
    // prepaid_amount, event_identifier). event_identifier is NULL for every imported row and the
    // unique index is NULLS NOT DISTINCT, so the effective per-company collision key is
    // (vendor_name, start_month, length_months, prepaid_amount) — two rows sharing it collide,
    // the last overwrites the others, and the amounts are NOT summed. That is the class of bug
    // that silently dropped $13K on the RTM pair. Warn (do NOT block, do NOT alter the rows).
    const keyOf = (c) => [
        String(c.vendor_name || '').trim(),
        c.start_month,
        c.length_months == null ? 'NULL' : c.length_months,
        c.prepaid_amount,
    ].join('||');
    const byKey = new Map();
    contracts.forEach((c, i) => {
        const k = keyOf(c);
        if (!byKey.has(k)) byKey.set(k, []);
        byKey.get(k).push(i);
    });
    for (const idxs of byKey.values()) {
        if (idxs.length <= 1) continue;
        const c = contracts[idxs[0]];
        const rowNums = idxs.map(i => contractRows[i]);
        flags.push({
            type: 'duplicate_key',
            severity: 'warning',
            row: contractRows[idxs[0]],
            label: `${idxs.length} prepaid rows collide on import`,
            message: `${idxs.length} prepaid rows for "${c.vendor_name}" (rows ${rowNums.join(', ')}) share the same contract key — start ${c.start_month}, length ${c.length_months == null ? 'indefinite' : c.length_months}, amount ${c.prepaid_amount} — and will collide on import: only the last is kept (amounts are NOT summed). Merge them into one row if this is a single contract, or vary a key field (e.g. the amount) if they are distinct.`,
        });
    }

    return { contracts, flags, specScan: { template: 'prepaid', layout: 'template', headerRow, cols: scanCols(col), monthCols: dateColumns, rows: rowsRaw } };
}

// ── Sheet selection ─────────────────────────────────────────

function pickExpensesSheet(workbook) {
    const names = workbook.SheetNames;
    const exact = names.find(n => /^expenses?$/i.test(n));
    if (exact) return exact;
    const contains = names.find(n => /expense/i.test(n));
    if (contains) return contains;
    // Fallback: first sheet that is NOT a prepaid sheet
    const notPrepaid = names.find(n => !/prepaid/i.test(n));
    return notPrepaid || names[0];
}
function pickPrepaidSheet(workbook) {
    const names = workbook.SheetNames;
    return names.find(n => /prepaid/i.test(n)) || null;
}

// ── Main entry ──────────────────────────────────────────────

/* Everything that happens AFTER the two sheets are parsed: vendor enrichment
 * (detectedFreq / forecastTypeDefault / last3Avg / lastPayment / lastPaymentMonth),
 * the cross-sheet orphan notice, flag sheet-tagging, and the result shape.
 *
 * EXTRACTED VERBATIM out of parseVendorExcel — not one rule changed — so that the
 * single-file path and the separate-files path (upload rework step 2) share ONE
 * assembly instead of growing a second. It is NOT optional post-processing:
 * buildStep6Vendors reads forecastTypeDefault / detectedFreq / last3Avg /
 * lastPayment / lastPaymentMonth off every vendor, and none of those exist in
 * parseExpensesSheet's raw output — a caller that skipped this would silently get
 * every vendor as constant/$0.
 */
export function assembleVendorResult(expResult, prepResult, expSheetName, prepSheetName) {
    const flags = [];

    for (const f of expResult.flags) flags.push({ ...f, sheet: expSheetName });
    for (const f of prepResult.flags) flags.push({ ...f, sheet: prepSheetName || '' });

    // Categories that should NEVER auto-classify as prepaid — spend here is
    // bursty by nature (trips, client dinners) and would generate spurious
    // quarterly/annual cycles otherwise.
    const SKIP_PREPAID_RE = /\btravel\b|\bmeals\b|\bentertainment\b|\bexpense report\b|reimbursement/i;

    // Enrich vendors with pattern + default forecast hints.
    const months = expResult.months || [];
    const vendors = (expResult.vendors || []).map(v => {
        const pattern = detectPattern(v.monthly_amounts, months);
        const avg3 = last3Avg(v.monthly_amounts, months);
        const lastAmt = lastNonZeroAmount(v.monthly_amounts, months);
        const lastMonth = lastNonZeroMonth(v.monthly_amounts, months);
        const nonZeroCount = months.filter(m => {
            const val = v.monthly_amounts[m];
            return typeof val === 'number' && val !== 0;
        }).length;
        const skipPrepaid = SKIP_PREPAID_RE.test(v.gl_category || '') || SKIP_PREPAID_RE.test(v.vendor_name || '');

        /* ★★ C2 — THE PREPAID ABSOLUTE RULE: A VENDOR IS PREPAID *IFF* IT APPEARS ON THE
         * PREPAID EXCEL TEMPLATE. THE PARSER NEVER INFERS IT FROM CADENCE.
         *
         * WHAT THIS USED TO DO: a non-monthly recurring cadence became the prepaid FLAG,
         * with the prepaid sheet never consulted:
         *     else if (freq === 'quarterly' || 'semi_annual' || 'annual')
         *         forecast_type_default = 'prepaid';        // ← the inference, deleted
         * A spending SHAPE was being read as an accounting TREATMENT. The result was a
         * contractless prepaid vendor, which books $0 FOREVER (computeVendor sums
         * monthlyData and never branches on the flag; runFullModel §12b books from stored
         * contracts, of which it has none) AND is filtered out of the Step-6 forecast
         * editor — so the operator could neither see it nor fix it. MEASURED: 6 such
         * vendors on the AE Model workbook, every one of which cid=3 (the oracle) stores
         * as `constant`. Prepaid now comes ONLY from the template force in
         * buildStep6Vendors (the `if (prepaidMatch)` branch).
         *
         * forecast_type_default uses engine enums (match vendors.forecast_type CHECK):
         *   constant | prepaid | pct_revenue | one_off | per_seat
         * ★ 'prepaid' IS NO LONGER A REACHABLE VALUE HERE, BY CONSTRUCTION. */
        let forecast_type_default = 'one_off';
        if (pattern.freq === 'monthly') {
            forecast_type_default = 'constant';
        } else if (pattern.freq === 'quarterly' || pattern.freq === 'semi_annual' || pattern.freq === 'annual') {
            /* ★ THE TRAVEL/MEALS GATE IS PRESERVED EXACTLY, AND IT HAD TO MOVE TO DO SO.
             * It used to read `if (skipPrepaid && forecast_type_default === 'prepaid')`,
             * i.e. it keyed on a value ONLY this branch could produce — so it applied to
             * the non-monthly cadences and NEVER to monthly. Rerouting this branch to
             * 'constant' while leaving the gate keyed on the type would have silently
             * stopped it firing (travel/meals non-monthly vendors would flip one_off →
             * constant); re-keying it to === 'constant' would have over-fired instead,
             * demoting MONTHLY travel/meals vendors that it does not touch today. Folding
             * it into this branch reproduces its exact historical effect: non-monthly +
             * travel/meals → one_off, monthly + travel/meals → constant, unchanged. */
            forecast_type_default = skipPrepaid ? 'one_off' : 'constant';
        }
        // Hard gate, unchanged: fewer than 2 non-zero months can't imply a cadence.
        if (nonZeroCount < 2) forecast_type_default = 'one_off';

        return {
            vendor_name: v.vendor_name,
            gl_category: v.gl_category,
            display_group: v.display_group ?? null, // canonical column (blank → null); vestigial for legacy
            // canonical per-line column; null on legacy layouts (see the push above)
            gl_line_name: v.gl_line_name ?? null,
            monthly_amounts: v.monthly_amounts,
            detectedFreq: pattern.freq,
            forecastTypeDefault: forecast_type_default,
            paymentMonths: pattern.paymentMonths,
            last3Avg: avg3,
            lastPayment: lastAmt,
            lastPaymentMonth: lastMonth,
        };
    });

    // ── Import-integrity notice (non-blocking): CROSS-SHEET ORPHAN ──
    // A prepaid vendor with no matching Expenses line. Both sheets parse in this one pass, so the
    // check is cheap here. INFORMATIONAL, not an error: it is EXPECTED for historical / archived
    // contracts (the intentional _archive_* orphans), and never blocks the import.
    const expenseNames = new Set(vendors.map(v => String(v.vendor_name || '').trim().toLowerCase()).filter(Boolean));
    const seenPrepaid = new Set();
    for (const c of (prepResult.contracts || [])) {
        const norm = String(c.vendor_name || '').trim().toLowerCase();
        if (!norm || seenPrepaid.has(norm)) continue;
        seenPrepaid.add(norm);
        if (!expenseNames.has(norm)) {
            flags.push({
                type: 'orphan_vendor',
                severity: 'info',
                row: 0,
                sheet: prepSheetName || '',
                label: `Prepaid vendor "${c.vendor_name}" has no matching Expenses line`,
                message: `Prepaid vendor "${c.vendor_name}" has no matching Expenses line — this is expected for historical or archived contracts and does not block import.`,
            });
        }
    }

    return {
        vendors,
        prepaidContracts: prepResult.contracts || [],
        months,
        flags,
        sheets: { expenses: expSheetName, prepaid: prepSheetName },
        // W-068 -- what the template check reads, per sheet (null for a legacy / absent sheet).
        specScans: { vendor: expResult.specScan || null, prepaid: prepResult.specScan || null },
    };
}

// ── Main entry: ONE workbook holding both tabs (the "Single file" upload mode).
// Behaviour unchanged — the pickers and the sheet parsers are untouched; the tail
// it used to inline is now assembleVendorResult above.
export function parseVendorExcel(arrayBuffer) {
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });

    const expSheetName = pickExpensesSheet(workbook);
    const prepSheetName = pickPrepaidSheet(workbook);

    const expResult = parseExpensesSheet(workbook.Sheets[expSheetName]);
    const prepResult = prepSheetName
        ? parsePrepaidSheet(workbook.Sheets[prepSheetName])
        : { contracts: [], flags: [] };

    return assembleVendorResult(expResult, prepResult, expSheetName, prepSheetName);
}

/* ONE sheet out of its OWN file (the "Separate files" upload mode).
 * Positional — SheetNames[0] — exactly like parseFinancialExcel.parseSingleSheet,
 * and for the same reason: in separate mode the operator's slot choice DECLARES
 * what the file is, so there is nothing to detect and neither picker runs. That
 * is the structural win of separate mode.
 * Returns the raw sheet-parser output plus the sheet name; the caller passes both
 * halves to assembleVendorResult so the two modes converge on one shape.
 */
export function parseVendorSheet(arrayBuffer, kind) {
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const parsed = kind === 'prepaid' ? parsePrepaidSheet(sheet) : parseExpensesSheet(sheet);
    return { ...parsed, sheetName };
}

export default parseVendorExcel;
